import { State as GlobalState } from 'store';
import { Options } from '../interfaces';
import { State } from './reducer';

function sortAndFilterIds<T>(
  state: State<T>,
  ids: string[],
  get: (state: State<T>, id: string) => T,
  options?: Options<T>,
): string[] {
  if (!options) {
    return ids;
  }

  const filteredIds = options.filters
    ? ids.filter(id => {
        const entity = get(state, id);
        return options.filters.every(f => f(entity));
      })
    : ids;
  return options.sorter ? filteredIds.sort((a, b) => options.sorter(get(state, a), get(state, b))) : filteredIds;
}

export function getEntityIds<T>(state: State<T>, options?: Options<T>): string[] {
  const ids = (state.data && state.data.allIds) || [];
  return sortAndFilterIds(state, ids, getEntity, options);
}

export function getUpdatedEntityIds<T>(state: State<T>, options?: Options<T>): string[] {
  const savedIds = (state.data && state.data.allIds) || [];
  const newIds = (state.updates && state.updates.new && Object.keys(state.updates.new).sort()) || [];
  return sortAndFilterIds(state, savedIds.concat(newIds), getUpdatedEntity, options);
}

export function getEntity<T>(state: State<T>, id: string): T {
  return (state.data && state.data.byId && state.data.byId[id]) || undefined;
}
export function getUpdatedEntity<T>(state: State<T>, id: string): T {
  const newEntity = state.updates && state.updates.new && state.updates.new[id];
  if (newEntity) {
    return newEntity;
  }

  const entity = getEntity<T>(state, id);
  if (!entity) {
    return undefined;
  }

  return Object.assign({}, entity, state.updates.changes[id]);
}

export function getEntities<T>(state: State<T>, options?: Options<T>): T[] {
  const ids = getEntityIds<T>(state, options);
  return ids.map(id => getEntity<T>(state, id));
}

export function getUpdatedEntities<T>(state: State<T>, options?: Options<T>): T[] {
  const ids = getUpdatedEntityIds<T>(state, options);
  return ids.map(id => getUpdatedEntity<T>(state, id));
}

export function getEntityMap<T>(state: State<T>): { [id: string]: T } {
  return state.data.byId;
}

export function getNewEntityMap<T>(state: State<T>): { [id: string]: T } {
  return state.updates.new;
}

export function hasChanges<T>(state: State<T>, id: string): boolean {
  return !!state.updates && !!state.updates.changes[id];
}

export function isNew<T>(state: State<T>, id: string): boolean {
  return !!state.updates && !!state.updates.new[id];
}

export function isRefreshing<T>(state: State<T>): boolean {
  return state.status.retrievingAll;
}

export function isSaving<T>(state: State<T>, id: string): boolean {
  return state.status.saving[id] === true;
}

export function selectors<T>(stateConverter: (state: GlobalState) => State<T>) {
  return {
    getEntityIds: (state: GlobalState, options?: Options<T>) => getEntityIds<T>(stateConverter(state), options),

    getUpdatedEntityIds: (state: GlobalState, options?: Options<T>) =>
      getUpdatedEntityIds<T>(stateConverter(state), options),

    getEntity: (state: GlobalState, id: string) => getEntity<T>(stateConverter(state), id),

    getUpdatedEntity: (state: GlobalState, id: string) => getUpdatedEntity<T>(stateConverter(state), id),

    getEntities: (state: GlobalState) => getEntities<T>(stateConverter(state)),

    getUpdatedEntities: (state: GlobalState) => getUpdatedEntities<T>(stateConverter(state)),

    getEntityMap: (state: GlobalState) => getEntityMap<T>(stateConverter(state)),

    getNewEntityMap: (state: GlobalState) => getNewEntityMap<T>(stateConverter(state)),

    hasChanges: (state: GlobalState, id: string) => hasChanges(stateConverter(state), id),

    isNew: (state: GlobalState, id: string) => isNew(stateConverter(state), id),
    isRefreshing: (state: GlobalState) => isRefreshing<T>(stateConverter(state)),
    isSaving: (state: GlobalState, id: string) => isSaving<T>(stateConverter(state), id),
  };
}
